/**
 * Particle Manager - Visual Effects System
 * 
 * Manages all particle effects for juice and polish:
 * - Launch burst particles
 * - Player trail
 * - Landing impact
 * - Star field parallax
 */

import Phaser from 'phaser';

export interface ParticleConfig {
  x: number;
  y: number;
  count?: number;
  color?: number;
  spread?: number;
  speed?: number;
  lifespan?: number;
}

/**
 * Particle Manager class
 */
export class ParticleManager {
  private scene: Phaser.Scene;
  private trailParticles: Phaser.GameObjects.Arc[] = [];
  private maxTrailParticles = 20;
  
  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.initTrailPool();
  }
  
  /**
   * Initialize trail particle pool
   */
  private initTrailPool(): void {
    for (let i = 0; i < this.maxTrailParticles; i++) {
      const particle = this.scene.add.circle(0, 0, 4, 0x00d4ff, 0.8);
      particle.setVisible(false);
      particle.setDepth(40);
      this.trailParticles.push(particle);
    }
  }
  
  /**
   * Create launch burst effect
   */
  createLaunchBurst(config: ParticleConfig): void {
    const { x, y, count = 15, color = 0x00d4ff, spread = 50, speed = 200, lifespan = 400 } = config;
    
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 / count) * i + Math.random() * 0.3;
      const dist = Math.random() * spread;
      
      const particle = this.scene.add.circle(x, y, 4 + Math.random() * 4, color, 0.8);
      particle.setDepth(45);
      
      const targetX = x + Math.cos(angle) * (dist + speed * 0.3);
      const targetY = y + Math.sin(angle) * (dist + speed * 0.3);
      
      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scaleX: 0.2,
        scaleY: 0.2,
        duration: lifespan,
        ease: 'Cubic.out',
        onComplete: () => {
          particle.destroy();
        }
      });
    }
  }
  
  /**
   * Create landing impact effect
   */
  createLandingImpact(config: ParticleConfig): void {
    const { x, y, count = 12, color = 0x00ff88, spread = 40 } = config;
    
    // Ring expansion
    const ring = this.scene.add.circle(x, y, 10, color, 0);
    ring.setStrokeStyle(3, color, 0.8);
    ring.setDepth(45);
    
    this.scene.tweens.add({
      targets: ring,
      radius: spread,
      alpha: 0,
      strokeAlpha: 0,
      duration: 400,
      ease: 'Cubic.out',
      onComplete: () => {
        ring.destroy();
      }
    });
    
    // Particles
    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 / count) * i;
      
      const particle = this.scene.add.circle(x, y, 3 + Math.random() * 3, color, 0.9);
      particle.setDepth(45);
      
      const dist = spread + Math.random() * 20;
      const targetX = x + Math.cos(angle) * dist;
      const targetY = y + Math.sin(angle) * dist;
      
      this.scene.tweens.add({
        targets: particle,
        x: targetX,
        y: targetY,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 500,
        ease: 'Cubic.out',
        onComplete: () => {
          particle.destroy();
        }
      });
    }
  }
  
  /**
   * Update player trail with velocity-based coloring
   * Higher velocity = more orange/red, lower = more cyan/blue
   */
  updateTrail(x: number, y: number, color: number, isFlying: boolean, velocity?: number): void {
    if (!isFlying) return;
    
    // Calculate velocity-based color (cyan -> orange based on speed)
    let trailColor = color;
    let trailSize = 4;
    let trailDuration = 300;
    
    if (velocity !== undefined) {
      // Normalize velocity (0-1 range, assuming max ~1200)
      const normalizedVel = Math.min(velocity / 1200, 1);
      
      // Color gradient: cyan (0x00d4ff) at slow, orange (0xff6600) at fast
      const r = Math.floor(0 + normalizedVel * 255);
      const g = Math.floor(212 - normalizedVel * 112); // 212 -> 100
      const b = Math.floor(255 - normalizedVel * 255); // 255 -> 0
      trailColor = (r << 16) | (g << 8) | b;
      
      // Size increases with velocity
      trailSize = 3 + normalizedVel * 4;
      
      // Faster = longer lasting trail
      trailDuration = 200 + normalizedVel * 200;
    }
    
    // Find an invisible trail particle
    for (const particle of this.trailParticles) {
      if (!particle.visible) {
        particle.setPosition(x, y);
        particle.setFillStyle(trailColor, 0.8);
        particle.setRadius(trailSize);
        particle.setVisible(true);
        particle.setAlpha(0.8);
        
        // Fade out
        this.scene.tweens.add({
          targets: particle,
          alpha: 0,
          scaleX: 0.3,
          scaleY: 0.3,
          duration: trailDuration,
          onComplete: () => {
            particle.setVisible(false);
            particle.setScale(1);
          }
        });
        
        break;
      }
    }
  }
  
  /**
   * Create score popup effect
   */
  createScorePopup(x: number, y: number, points: number, combo: number): void {
    const color = combo > 1 ? '#ffcc00' : '#ffffff';
    const text = combo > 1 ? `+${points} x${combo}` : `+${points}`;
    
    const popup = this.scene.add.text(x, y, text, {
      fontSize: '24px',
      fontFamily: '"Segoe UI", system-ui, sans-serif',
      color: color,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 2
    }).setOrigin(0.5).setDepth(100);
    
    this.scene.tweens.add({
      targets: popup,
      y: y - 60,
      alpha: 0,
      scaleX: 1.3,
      scaleY: 1.3,
      duration: 800,
      ease: 'Cubic.out',
      onComplete: () => {
        popup.destroy();
      }
    });
  }
  
  /**
   * Create screen shake effect
   */
  createScreenShake(intensity: number = 5, duration: number = 100): void {
    this.scene.cameras.main.shake(duration, intensity / 1000);
  }
  
  /**
   * Create game over effect
   */
  createGameOverEffect(x: number, y: number): void {
    // Explosion
    this.createLaunchBurst({
      x, y,
      count: 25,
      color: 0xff6b6b,
      spread: 80,
      speed: 300,
      lifespan: 600
    });
    
    // Screen shake
    this.createScreenShake(10, 200);
    
    // Flash
    this.scene.cameras.main.flash(200, 255, 100, 100);
  }
  
  /**
   * Cleanup
   */
  destroy(): void {
    for (const particle of this.trailParticles) {
      particle.destroy();
    }
    this.trailParticles = [];
  }
}

export default ParticleManager;
